import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hash, Loader2, QrCode } from 'lucide-react';
import { apiClient } from '../../utils/api';
import { QrScannerModal } from './QrScannerModal';

interface AppointmentQuickFindProps {
  /**
   * API path used to resolve QR / check-in code payloads.
   * When null, a short code is opened directly as the appointment path param
   * (the detail endpoint resolves check-in codes).
   */
  resolvePath?: string | null;
  /** Where to navigate after resolving an appointment id. Defaults to `/appointments/:id`. */
  appointmentPath?: (appointmentId: string) => string;
  onError?: (message: string) => void;
  onFound?: (appointmentId: string) => void;
  className?: string;
}

export function AppointmentQuickFind({
  resolvePath = '/api/admin/reception/resolve-qr',
  appointmentPath = (id) => `/appointments/${id}`,
  onError,
  onFound,
  className = '',
}: AppointmentQuickFindProps) {
  const navigate = useNavigate();
  const [checkInCode, setCheckInCode] = useState('');
  const [resolvingCode, setResolvingCode] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const openAppointment = (appointmentId: string) => {
    onFound?.(appointmentId);
    navigate(appointmentPath(appointmentId));
  };

  const handleResolveCheckInCode = async () => {
    const code = checkInCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,8}$/.test(code)) {
      onError?.('Saisissez un code d’accueil (3 à 8 caractères, lettres ou chiffres).');
      return;
    }

    if (!resolvePath) {
      setCheckInCode('');
      openAppointment(code);
      return;
    }

    setResolvingCode(true);
    try {
      const res = await apiClient(`${resolvePath}?payload=${encodeURIComponent(code)}`);
      const data = await res.json();
      if (!res.ok || !data.success || !data.data?.appointmentId) {
        throw new Error(data.message || 'Code non reconnu');
      }
      setCheckInCode('');
      openAppointment(data.data.appointmentId as string);
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Code d’accueil non reconnu');
    } finally {
      setResolvingCode(false);
    }
  };

  return (
    <>
      <div className={`flex flex-col sm:flex-row gap-2 ${className}`}>
        <div className="relative flex-1 min-w-[10rem]">
          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={8}
            className="input-field w-full pl-9 tracking-[0.3em] uppercase font-semibold"
            placeholder="Code AB12"
            value={checkInCode}
            onChange={(e) =>
              setCheckInCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase())
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleResolveCheckInCode();
              }
            }}
            aria-label="Code d'accueil du rendez-vous"
          />
        </div>
        <button
          type="button"
          className="btn-secondary px-4 disabled:opacity-50 whitespace-nowrap"
          disabled={resolvingCode || checkInCode.length < 3}
          onClick={() => void handleResolveCheckInCode()}
        >
          {resolvingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ouvrir'}
        </button>
        <button
          type="button"
          className="btn-primary px-4 flex items-center justify-center gap-2 whitespace-nowrap"
          onClick={() => setShowQr(true)}
        >
          <QrCode className="w-4 h-4" />
          QR
        </button>
      </div>

      <QrScannerModal
        isOpen={showQr}
        onClose={() => setShowQr(false)}
        onAppointmentFound={openAppointment}
        onError={onError}
        resolvePath={resolvePath}
      />
    </>
  );
}
